/**
 * Activity history page (FS-058).
 *
 * The filters are built from the server's facets, so every chip matches
 * something. The previous version hard-coded six chips, one of which was
 * `user` — a kind nothing had ever written, so that filter returned an empty
 * list forever.
 */

import { useState } from "react";
import { Clock, Search, X } from "lucide-react";
import {
  useActivityFacets,
  useActivityFeed,
  type HistorySource,
} from "@/features/activity/api";
import TimelineView from "@/features/activity/components/TimelineView";
import Card from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const PAGE_SIZE = 50;

const SOURCE_LABELS: Record<HistorySource, string> = {
  audit: "System activity",
  contact: "Customer contact",
};

/** Days back, or null for everything. */
const RANGES: { label: string; days: number | null }[] = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "All time", days: null },
];

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-[var(--radius-full)] border px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-brand-300 bg-brand-100 text-brand-700 dark:border-brand-700 dark:bg-brand-900/50 dark:text-brand-300"
          : "border-[var(--border-default)] bg-[var(--surface-base)] text-[var(--text-secondary)] hover:bg-[var(--color-surface-hover)]"
      }`}
    >
      {children}
    </button>
  );
}

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-16 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
        {label}
      </span>
      {children}
    </div>
  );
}

function ActivityFeedPage() {
  const [page, setPage] = useState(1);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [sources, setSources] = useState<HistorySource[]>([]);
  const [actorId, setActorId] = useState<string>("");
  const [days, setDays] = useState<number | null>(30);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");

  const { data: facets } = useActivityFacets();

  const since =
    days === null
      ? undefined
      : new Date(Date.now() - days * 86400_000).toISOString();

  const { data, isLoading } = useActivityFeed({
    page,
    pageSize: PAGE_SIZE,
    entityTypes: entityTypes.length ? entityTypes : undefined,
    sources: sources.length ? sources : undefined,
    actorId: actorId || undefined,
    since,
    search: search || undefined,
  });

  /** Any filter change resets to page 1 — page 3 of a new filter is nowhere. */
  function toggle<T>(list: T[], value: T, set: (next: T[]) => void) {
    set(
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
    );
    setPage(1);
  }

  const filtersActive =
    entityTypes.length > 0 ||
    sources.length > 0 ||
    actorId !== "" ||
    search !== "" ||
    days !== 30;

  function clearFilters() {
    setEntityTypes([]);
    setSources([]);
    setActorId("");
    setSearch("");
    setSearchDraft("");
    setDays(30);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Activity history
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          What the system recorded and what your people did with customers, in
          one timeline.
        </p>
      </div>

      <Card padding="sm">
        <div className="space-y-2.5">
          <FilterGroup label="Show">
            {(facets?.sources ?? []).map((source) => (
              <Chip
                key={source}
                active={sources.includes(source)}
                onClick={() => toggle(sources, source, setSources)}
              >
                {SOURCE_LABELS[source] ?? source}
              </Chip>
            ))}
          </FilterGroup>

          {facets?.entity_types.length ? (
            <FilterGroup label="About">
              {facets.entity_types.map((kind) => (
                <Chip
                  key={kind.key}
                  active={entityTypes.includes(kind.key)}
                  onClick={() => toggle(entityTypes, kind.key, setEntityTypes)}
                >
                  {kind.label}
                  <span className="ml-1 text-[var(--text-tertiary)]">
                    {kind.count}
                  </span>
                </Chip>
              ))}
            </FilterGroup>
          ) : null}

          <FilterGroup label="When">
            {RANGES.map((range) => (
              <Chip
                key={range.label}
                active={days === range.days}
                onClick={() => {
                  setDays(range.days);
                  setPage(1);
                }}
              >
                {range.label}
              </Chip>
            ))}
          </FilterGroup>

          <div className="flex flex-wrap items-center gap-2">
            <span className="w-16 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
              Who
            </span>
            <select
              value={actorId}
              onChange={(event) => {
                setActorId(event.target.value);
                setPage(1);
              }}
              aria-label="Filter by person"
              className="h-[30px] rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)]"
            >
              <option value="">Anyone</option>
              {(facets?.actors ?? []).map((actor) => (
                <option key={actor.id} value={actor.id}>
                  {actor.name}
                </option>
              ))}
            </select>

            <form
              className="flex items-center gap-1.5"
              onSubmit={(event) => {
                event.preventDefault();
                setSearch(searchDraft.trim());
                setPage(1);
              }}
            >
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-tertiary)]" />
                <input
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder="Search records and notes"
                  aria-label="Search history"
                  className="h-[30px] w-56 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] pl-7 pr-2 text-xs text-[var(--text-primary)]"
                />
              </div>
              <Button type="submit" variant="secondary" size="sm">
                Search
              </Button>
            </form>

            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-xs text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
              >
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        </div>
      </Card>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : data && data.items.length > 0 ? (
        <>
          <Card padding="none">
            <div className="py-2">
              <TimelineView
                entries={data.items}
                showEntity
                kinds={facets?.entity_types}
                counted={`${data.counted} ${data.total} ${
                  data.total === 1 ? "entry" : "entries"
                }.`}
              />
            </div>
          </Card>
          {data.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-[var(--text-tertiary)]">
                Page {data.page} of {data.pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= data.pages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Clock />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              {filtersActive
                ? "Nothing matches these filters"
                : "No activity yet"}
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {data?.counted ??
                "Actions will appear here as you use the application."}
            </p>
            {filtersActive && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={clearFilters}
              >
                Clear filters
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

export default ActivityFeedPage;
